import { fetchWithSupabaseAuth } from "./supabaseClient.js";
import {
  assertValidUserUiPreferencesPatch,
  assertValidUserUiPreferencesPayload,
} from "../utils/userUiPreferences.js";

export const USER_UI_PREFERENCES_LEGACY_ENDPOINT = "/api/me/ui-preferences";
export const USER_UI_PREFERENCES_V2_ENDPOINT = `${USER_UI_PREFERENCES_LEGACY_ENDPOINT}/v2`;
export const USER_UI_PREFERENCES_V3_ENDPOINT = `${USER_UI_PREFERENCES_LEGACY_ENDPOINT}/v3`;
export const USER_UI_PREFERENCES_V4_ENDPOINT = `${USER_UI_PREFERENCES_LEGACY_ENDPOINT}/v4`;
export const USER_UI_PREFERENCES_V5_ENDPOINT = `${USER_UI_PREFERENCES_LEGACY_ENDPOINT}/v5`;
export const USER_UI_PREFERENCES_V6_ENDPOINT = `${USER_UI_PREFERENCES_LEGACY_ENDPOINT}/v6`;
export const USER_UI_PREFERENCES_V7_ENDPOINT = `${USER_UI_PREFERENCES_LEGACY_ENDPOINT}/v7`;
export const USER_UI_PREFERENCES_V8_ENDPOINT = `${USER_UI_PREFERENCES_LEGACY_ENDPOINT}/v8`;
export const USER_UI_PREFERENCES_ENDPOINT = USER_UI_PREFERENCES_V8_ENDPOINT;
export const USER_UI_PREFERENCES_CLAIM_MISSING_ENDPOINT = `${USER_UI_PREFERENCES_ENDPOINT}/claim-missing`;

export class UserUiPreferencesApiError extends Error {
  constructor(message, {
    code = "request_failed",
    payload = null,
    status = null,
  } = {}) {
    super(message);
    this.name = "UserUiPreferencesApiError";
    this.code = code;
    this.payload = payload;
    this.status = Number.isFinite(status) ? Number(status) : null;
  }
}

function resolveIssueMessage(issue) {
  if (typeof issue === "string") {
    return issue.trim();
  }

  if (!issue || typeof issue !== "object") {
    return "";
  }

  const message = typeof issue.msg === "string"
    ? issue.msg.trim()
    : (typeof issue.message === "string" ? issue.message.trim() : "");
  if (!message) {
    return "";
  }

  const location = Array.isArray(issue.loc)
    ? issue.loc.filter((part) => part !== "body").map(String).filter(Boolean).join(" › ")
    : "";
  return location ? `${location}: ${message}` : message;
}

export function resolveUserUiPreferencesApiErrorMessage(payload, fallbackMessage) {
  if (typeof payload?.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  if (Array.isArray(payload?.detail)) {
    const issues = payload.detail.map(resolveIssueMessage).filter(Boolean).slice(0, 3);
    if (issues.length > 0) {
      return `${fallbackMessage} — ${issues.join(" · ")}`;
    }
  }

  return fallbackMessage;
}

async function requestUserUiPreferences({
  body,
  endpoint = USER_UI_PREFERENCES_ENDPOINT,
  fetchImpl,
  method = "GET",
  signal,
} = {}) {
  const fallbackMessage = method === "PATCH" || method === "POST"
    ? "Impossible d’enregistrer les préférences d’interface."
    : "Impossible de charger les préférences d’interface.";
  const response = await fetchWithSupabaseAuth(endpoint, {
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
    fetchImpl,
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    method,
    signal,
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new UserUiPreferencesApiError(
      resolveUserUiPreferencesApiErrorMessage(payload, fallbackMessage),
      {
        code: response.status === 401 ? "unauthorized" : "request_failed",
        payload,
        status: response.status,
      },
    );
  }

  try {
    return assertValidUserUiPreferencesPayload(payload);
  } catch (error) {
    throw new UserUiPreferencesApiError(
      "La réponse des préférences d’interface est invalide.",
      {
        code: "invalid_payload",
        payload,
        status: response.status,
      },
    );
  }
}

export function fetchUserUiPreferences({ fetchImpl, signal } = {}) {
  return requestUserUiPreferences({
    fetchImpl,
    method: "GET",
    signal,
  });
}

export async function patchUserUiPreferences(patch, { fetchImpl, signal } = {}) {
  const normalizedPatch = assertValidUserUiPreferencesPatch(patch);
  return requestUserUiPreferences({
    body: normalizedPatch,
    fetchImpl,
    method: "PATCH",
    signal,
  });
}

export async function claimMissingUserUiPreferences(patch, { fetchImpl, signal } = {}) {
  const normalizedPatch = assertValidUserUiPreferencesPatch(patch);
  return requestUserUiPreferences({
    body: normalizedPatch,
    endpoint: USER_UI_PREFERENCES_CLAIM_MISSING_ENDPOINT,
    fetchImpl,
    method: "POST",
    signal,
  });
}
